/**
 * Demo Mode Entry Point
 *
 * Initializes the app in demo mode with the PixelForge Games scenario:
 * - Demo workspace context (PixelForge Games)
 * - Only demo data visible
 * - Visual indicator showing "Demo Mode"
 *
 * Seed data: supabase/DEMO_PIXELFORGE.sql
 * Demo script: DEMO_SCRIPT_PIXELFORGE.md
 * Access via: /demo
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, Sparkles, CheckCircle } from 'lucide-react';
import { setTenantContext, DEMO_WORKSPACE_ID } from '../lib/tenantContext';
import { UI_LABELS } from '../lib/terminology';

export default function DemoMode() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState('Initializing demo environment...');

  useEffect(() => {
    async function initDemo() {
      try {
        // Set the tenant context to the demo workspace
        setTenantContext({
          workspaceId: DEMO_WORKSPACE_ID,
          workspaceName: 'PixelForge Games (Demo)',
          userId: 'demo-user',
          userEmail: 'priya.nair@pixelforge.gg',
        });

        // Store demo mode flag in sessionStorage
        sessionStorage.setItem('peakPriorities_demoMode', 'true');
        sessionStorage.setItem('peakPriorities_demoWorkspaceId', DEMO_WORKSPACE_ID);

        setMessage('Demo environment ready!');
        setStatus('ready');

        // Redirect to the prioritization app after a brief delay
        setTimeout(() => {
          navigate('/prioritization', { replace: true });
        }, 1500);

      } catch (error) {
        console.error('[DemoMode] Error initializing:', error);
        setStatus('error');
        setMessage('Failed to initialize demo environment');
      }
    }

    initDemo();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center p-4 bg-[var(--mustard)]/20 mb-4">
            <Gamepad2 className="h-12 w-12 text-[var(--mustard)]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Peak Priorities</h1>
          <p className="text-[var(--text-ter)]">Demo Environment · PixelForge Games</p>
        </div>

        {/* Status Card */}
        <div className="bg-[var(--bg-raised)]/50 backdrop-blur border border-[var(--rule)] p-8 mb-6">
          {status === 'loading' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="h-8 w-8 border-2 border-[var(--mustard)] border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-[var(--text-ter)]">{message}</p>
            </>
          )}

          {status === 'ready' && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-[var(--green)]" />
              </div>
              <p className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">{message}</p>
              <p className="text-[var(--text-ter)] text-sm mt-2">Redirecting to dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <p className="text-[var(--coral)] font-medium">{message}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-white transition-colors"
              >
                Try Again
              </button>
            </>
          )}
        </div>

        {/* Demo Info */}
        <div className="bg-[var(--mustard)]/10 border border-[var(--mustard)]/30 p-4">
          <div className="flex items-center justify-center gap-2 text-[var(--mustard)] mb-2">
            <Sparkles className="h-4 w-4" />
            <span className="font-medium">Demo Mode</span>
          </div>
          <p className="text-sm text-[var(--mustard)]/70">
            You're entering the PixelForge Games demo scenario.
            Grandma's Heist has 8M players and a very complicated Q2.
            All data is sample data for demonstration purposes.
          </p>
        </div>

        {/* Demo Scenario Summary */}
        <div className="mt-6 text-left bg-[var(--bg-raised)]/30 p-4">
          <h3 className="text-sm font-medium text-[var(--text-ter)] mb-3">Demo Scenario: PixelForge Games Q2 2026</h3>
          <ul className="text-xs text-[var(--text-ter)] space-y-1.5">
            <li>• <strong>21 items</strong> — intake chaos, ready work, and one deferred VR dream</li>
            <li>• <strong>1 active cycle</strong> in the PRIORITIZE phase (it's been a journey)</li>
            <li>• <strong>2 tracks</strong> — Platform & Infrastructure vs. Player Experience</li>
            <li>• <strong>12-item ranked {UI_LABELS.itemPool}</strong> with a clear cut line</li>
            <li>• <strong>8 team members</strong> with very different opinions</li>
            <li>• <strong>4 decisions on record</strong> including one NFT rejection and one classified CTO project</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
